import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, Users, Plus, LogIn, Megaphone, MessageSquare, FolderOpen, Gauge, Trash2,
  Copy, Check, ExternalLink, UserPlus, BookOpen, ClipboardCheck, Activity, Crown, Lock, FileDown,
} from 'lucide-react'
import { teacherGroupApi, type TGroup, type TMember, type TPost, type TResource, type TSelfAssess, type TSummary, type TEvidence } from './api'
import { COMPETENCIES, DOMAIN_NAMES, LEVEL_LABELS } from './framework'
import { priceFor, fmtVnd, GROUP_PLAN } from './pricing'
import NcbhTab from './NcbhTab'
import { useAuth } from '../../contexts/AuthContext'

type Tab = 'posts' | 'ncbh' | 'resources' | 'competency' | 'members'
type ResourceSource = 'external' | 'ebook-lesson' | 'ebook-course' | 'pmaker' | 'eng'

const LEVEL_TONE = ['bg-gray-100 text-gray-500', 'bg-sky-100 text-sky-700', 'bg-amber-100 text-amber-800', 'bg-emerald-100 text-emerald-800']
const SUBJECT_OPTIONS = ['Vật lí', 'Toán', 'Hóa học', 'Sinh học', 'Tin học', 'Công nghệ', 'Ngữ văn', 'Lịch sử', 'Địa lí', 'Tiếng Anh', 'Liên môn', 'Khác']
const normalizeText = (s?: string) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
const isPhysicsSubject = (s?: string) => {
  const n = normalizeText(s)
  return n.includes('vat li') || n.includes('vat ly') || n.includes('physics')
}

const TeacherGroupPage: React.FC = () => {
  const [groups, setGroups] = useState<TGroup[] | null>(null)
  const [sel, setSel] = useState<string>('')   // group id đang mở
  const reload = () => teacherGroupApi.listGroups().then(setGroups).catch(() => setGroups([]))
  useEffect(() => { reload() }, [])

  return (
    <div className="min-h-screen bg-[#fdfcf0]">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-5">
          <Link to="/teacher/elearning" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
            <ArrowLeft size={16} /> Bảng điều khiển
          </Link>
          <span className="text-slate-300">/</span>
          <span className="inline-flex items-center gap-1.5 font-black text-slate-800"><Users size={18} className="text-teal-600" /> Tổ chuyên môn</span>
        </div>

        {!sel
          ? <GroupList groups={groups} onOpen={setSel} onChanged={reload} />
          : <GroupDetail gid={sel} onBack={() => { setSel(''); reload() }} />}
      </div>
    </div>
  )
}

// ---------------- Danh sách tổ + tạo/tham gia ----------------
const GroupList: React.FC<{ groups: TGroup[] | null; onOpen: (id: string) => void; onChanged: () => void }> = ({ groups, onOpen, onChanged }) => {
  const [name, setName] = useState(''); const [subject, setSubject] = useState(''); const [school, setSchool] = useState('')
  const [code, setCode] = useState(''); const [msg, setMsg] = useState('')

  const create = async () => {
    if (!name.trim()) { setMsg('Nhập tên tổ.'); return }
    try { const g = await teacherGroupApi.createGroup({ name, subject, school }); setName(''); setSubject(''); setSchool(''); onChanged(); onOpen(g.id) }
    catch { setMsg('Không tạo được tổ (cần đăng nhập).') }
  }
  const join = async () => {
    if (!code.trim()) return
    try { const g = await teacherGroupApi.join(code.trim()); setCode(''); onChanged(); onOpen(g.id) }
    catch { setMsg('Mã tham gia không đúng.') }
  }

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-teal-50 to-sky-50 rounded-2xl border border-teal-100 p-4">
        <div className="font-black text-slate-900 text-lg">Không gian sinh hoạt chuyên môn dùng chung cho mọi môn</div>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">Bảng tin, chuyên đề NCBH, phiếu dự giờ, kho học liệu dạng link và hồ sơ năng lực số đều dùng được cho Toán, Hóa, Sinh, Tin, Công nghệ, liên môn... Riêng tổ Vật lí có thêm lối lấy nguồn nội bộ từ Ebook, PMaker và Eng.</p>
        <div className="grid sm:grid-cols-3 gap-2 mt-3 text-xs">
          <div className="rounded-xl bg-white/80 border border-white p-2"><b>5.1 Giao tiếp</b><br />Thông báo, thảo luận, kênh chính thức của tổ.</div>
          <div className="rounded-xl bg-white/80 border border-white p-2"><b>5.2 Hợp tác</b><br />Chuyên đề NCBH, dự giờ, góp ý cải tiến.</div>
          <div className="rounded-xl bg-white/80 border border-white p-2"><b>5.3 Học liệu</b><br />Chia sẻ link, học liệu số và nguồn nội bộ khi có.</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-teal-100 p-4">
          <div className="font-black text-slate-800 mb-2 flex items-center gap-1.5"><Plus size={16} className="text-teal-600" /> Tạo tổ chuyên môn</div>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tên tổ (vd: Tổ Vật lí – THPT ...)" className="w-full p-2 mb-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
          <div className="flex gap-2 mb-2">
            <div className="flex-1">
              <input list="teacher-group-subjects" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Môn / liên môn" className="w-full p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
              <datalist id="teacher-group-subjects">
                {SUBJECT_OPTIONS.map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
            <input value={school} onChange={(e) => setSchool(e.target.value)} placeholder="Trường" className="flex-1 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {SUBJECT_OPTIONS.slice(0, 6).map(s => (
              <button key={s} type="button" onClick={() => setSubject(s)} className={`px-2 py-0.5 rounded-full border text-[11px] font-bold ${subject === s ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-gray-200 text-slate-500 hover:border-teal-300'}`}>{s}</button>
            ))}
          </div>
          <button onClick={create} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-bold hover:bg-teal-700"><Plus size={15} /> Tạo tổ</button>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="font-black text-slate-800 mb-2 flex items-center gap-1.5"><LogIn size={16} className="text-sky-600" /> Tham gia bằng mã</div>
          <p className="text-xs text-slate-500 mb-2">Nhập mã do tổ trưởng cung cấp để tham gia tổ.</p>
          <div className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="MÃ THAM GIA" className="flex-1 p-2 border border-gray-200 rounded-lg text-sm font-mono tracking-wider outline-none focus:ring-2 focus:ring-sky-200" />
            <button onClick={join} className="px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-bold hover:bg-sky-700">Tham gia</button>
          </div>
        </div>
      </div>
      {msg && <p className="text-sm text-rose-600">{msg}</p>}

      <div>
        <div className="text-xs font-bold text-slate-500 uppercase mb-2">Tổ của tôi</div>
        {groups === null ? <p className="text-sm text-gray-400">Đang tải…</p>
          : groups.length === 0 ? <p className="text-sm text-gray-400 p-4 text-center border border-dashed rounded-xl">Chưa có tổ nào. Tạo tổ hoặc tham gia bằng mã.</p>
          : <div className="grid sm:grid-cols-2 gap-3">
              {groups.map((g) => (
                <button key={g.id} onClick={() => onOpen(g.id)} className="text-left bg-white rounded-xl border border-gray-100 hover:border-teal-300 hover:shadow-sm transition p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-black text-slate-800">{g.name}</span>
                    {g.isLeader && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">Tổ trưởng</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{[g.subject, g.school].filter(Boolean).join(' · ') || '—'}</div>
                  <div className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Users size={12} /> {g.memberCount} thành viên</div>
                </button>
              ))}
            </div>}
      </div>
    </div>
  )
}

// ---------------- Chi tiết tổ ----------------
const GroupDetail: React.FC<{ gid: string; onBack: () => void }> = ({ gid, onBack }) => {
  const { user } = useAuth()
  const [g, setG] = useState<(TGroup & { members: TMember[]; isOwner?: boolean }) | null>(null)
  const [tab, setTab] = useState<Tab>('posts')
  const [copied, setCopied] = useState(false)
  const [upMsg, setUpMsg] = useState('')
  const [showUp, setShowUp] = useState(false)
  const [qr, setQr] = useState<any>(null)
  const load = () => teacherGroupApi.groupDetail(gid).then(setG).catch(() => setG(null))
  useEffect(() => { load() }, [gid])

  if (!g) return <p className="text-sm text-gray-400">Đang tải tổ…</p>

  const isAdmin = user?.role === 'admin'
  const physicsGroup = isPhysicsSubject(g.subject)
  const copyCode = async () => { try { await navigator.clipboard.writeText(g.join_code) } catch {}; setCopied(true); setTimeout(() => setCopied(false), 1500) }
  const upgrade = () => { setQr(null); setShowUp(true) }
  const sendRequest = async () => {
    try { await teacherGroupApi.requestUpgrade(gid); setShowUp(false); setUpMsg('✅ Đã gửi yêu cầu — quản trị sẽ liên hệ thanh toán & kích hoạt Gói Tổ.') }
    catch { setUpMsg('Gửi yêu cầu chưa thành công, thử lại sau.') }
  }
  const doCheckout = async () => { try { setQr(await teacherGroupApi.checkout(gid)) } catch { setUpMsg('Tạo thanh toán chưa thành công.') } }
  const adminGrant = async () => {
    const seats = Number(window.prompt('Kích hoạt PRO — số GV tối đa (đã thanh toán):', String(Math.max(6, g.members.length))) || 0)
    if (!seats) return
    try { await teacherGroupApi.adminGrantPro(gid, { plan: 'pro', months: 12, max_members: seats }); load(); setUpMsg('✅ Đã kích hoạt Gói Tổ PRO.') }
    catch { setUpMsg('Cấp gói thất bại.') }
  }
  const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'posts', label: 'Bảng tin & thảo luận', icon: <Megaphone size={15} /> },
    { id: 'ncbh', label: 'Chuyên đề (NCBH)', icon: <ClipboardCheck size={15} /> },
    { id: 'resources', label: 'Kho học liệu chung', icon: <FolderOpen size={15} /> },
    { id: 'competency', label: 'Năng lực số (Miền 5)', icon: <Gauge size={15} /> },
    { id: 'members', label: 'Thành viên', icon: <Users size={15} /> },
  ]

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={15} /> Danh sách tổ</button>
      <div className="bg-white rounded-2xl border border-teal-100 p-4 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-black text-lg text-slate-900">{g.name}</span>
              {g.isPro
                ? <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><Crown size={11} /> GÓI PRO</span>
                : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Miễn phí</span>}
            </div>
            <div className="text-xs text-slate-500">{[g.subject, g.school].filter(Boolean).join(' · ') || '—'} · {g.members.length}/{g.isPro ? g.maxMembers : g.maxMembers + ' (giới hạn)'} thành viên</div>
          </div>
          <div className="flex items-center gap-2">
            {g.isLeader && !g.isPro && (
              <button onClick={upgrade} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600"><Crown size={14} /> Nâng cấp Gói Tổ</button>
            )}
            {isAdmin && (
              <button onClick={adminGrant} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-400 text-amber-700 text-sm font-bold hover:bg-amber-50">{g.isPro ? 'Gia hạn PRO' : 'Kích hoạt PRO'}</button>
            )}
            <button onClick={copyCode} title="Sao chép mã tham gia" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-300 bg-teal-50 text-teal-700 text-sm font-bold hover:bg-teal-100">
              {copied ? <Check size={14} /> : <Copy size={14} />} Mã: <span className="font-mono tracking-wider">{g.join_code}</span>
            </button>
          </div>
        </div>
        <div className={`mt-3 rounded-xl border p-3 ${physicsGroup ? 'bg-indigo-50 border-indigo-100' : 'bg-slate-50 border-slate-100'}`}>
          <div className="text-xs font-black uppercase tracking-wide text-slate-500">Mức hỗ trợ môn học</div>
          <div className="text-sm text-slate-700 mt-1">
            {physicsGroup
              ? <>Tổ Vật lí có thể dùng đủ nguồn nội bộ: <b>Ebook</b>, <b>PMaker</b>, <b>Eng</b> và link ngoài.</>
              : <>Tổ môn này dùng đầy đủ bảng tin, NCBH, phiếu dự giờ, hồ sơ năng lực và kho link ngoài. Học liệu nội bộ theo môn sẽ mở rộng dần sau.</>}
          </div>
        </div>
        {upMsg && <p className="text-xs text-amber-700 mt-2">{upMsg}</p>}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold border transition ${tab === t.id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-gray-200 hover:border-teal-300'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'posts' && <PostsTab gid={gid} />}
      {tab === 'ncbh' && <NcbhTab gid={gid} isPro={g.isPro} onUpgrade={upgrade} />}
      {tab === 'resources' && <ResourcesTab gid={gid} group={g} />}
      {tab === 'competency' && <CompetencyTab gid={gid} isPro={g.isPro} onUpgrade={upgrade} />}
      {tab === 'members' && <MembersTab g={g} onChanged={load} />}

      {showUp && (() => {
        const n = g.members.length
        const extra = Math.max(0, n - GROUP_PLAN.freeSeats)
        return (
          <div className="fixed inset-0 z-[300] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowUp(false)}>
            <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-1"><Crown size={18} className="text-amber-500" /><span className="font-black text-lg text-slate-900">Nâng cấp Gói Tổ chuyên môn</span></div>
              <p className="text-sm text-slate-500 mb-3">Mở khoá đủ 5 miền năng lực · Chuyên đề NCBH + phiếu dự giờ · Dashboard tổ · Minh chứng tự động · Xuất hồ sơ.</p>
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm">
                <div className="flex justify-between"><span>Gói cơ bản (≤{GROUP_PLAN.freeSeats} GV)/năm</span><b>{fmtVnd(GROUP_PLAN.base)}</b></div>
                {extra > 0 && <div className="flex justify-between mt-1"><span>{extra} GV thêm × {fmtVnd(GROUP_PLAN.perExtra)}</span><b>{fmtVnd(extra * GROUP_PLAN.perExtra)}</b></div>}
                <div className="flex justify-between mt-2 pt-2 border-t border-amber-200 text-base"><span className="font-bold">Tổng ({n} GV)/năm</span><b className="text-amber-700">{fmtVnd(priceFor(n))}</b></div>
              </div>
              {qr ? (
                <div className="mt-3 rounded-xl border border-teal-200 bg-teal-50/50 p-3 text-center">
                  {qr.vietqr?.qr_image
                    ? <img src={qr.vietqr.qr_image} alt="VietQR" className="mx-auto w-52 h-52 object-contain rounded-lg bg-white border" />
                    : <p className="text-xs text-rose-600">Chưa cấu hình tài khoản ngân hàng (VietQR) trên máy chủ.</p>}
                  <div className="text-xs text-slate-600 mt-2">Chuyển khoản đúng số tiền <b>{fmtVnd(qr.amount_vnd)}</b></div>
                  <div className="text-xs text-slate-600">Nội dung CK: <b className="font-mono">{qr.memo}</b> {qr.vietqr && <>· {qr.vietqr.bank_id} {qr.vietqr.account}</>}</div>
                  <p className="text-[11px] text-slate-400 mt-1">{qr.auto ? 'Hệ thống tự kích hoạt PRO ngay khi nhận được tiền (SePay).' : 'Sau khi chuyển khoản, quản trị sẽ kích hoạt gói.'}</p>
                  <button onClick={() => setShowUp(false)} className="mt-2 text-sm font-bold text-teal-700">Đã hiểu, đóng</button>
                </div>
              ) : (<>
                <p className="text-[11px] text-slate-400 mt-2">Thanh toán bằng QR (tự kích hoạt qua SePay) hoặc gửi yêu cầu để quản trị liên hệ.</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setShowUp(false)} className="px-3 py-2 rounded-lg border border-gray-200 text-slate-600 text-sm font-bold hover:bg-gray-50">Để sau</button>
                  <button onClick={sendRequest} className="flex-1 px-3 py-2 rounded-lg border border-amber-300 text-amber-700 text-sm font-bold hover:bg-amber-50">Gửi yêu cầu</button>
                  <button onClick={doCheckout} className="flex-1 px-3 py-2 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600">Thanh toán QR</button>
                </div>
              </>)}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// ---- 5.1 Bảng tin & thảo luận ----
const PostsTab: React.FC<{ gid: string }> = ({ gid }) => {
  const [posts, setPosts] = useState<TPost[]>([])
  const [kind, setKind] = useState<'notice' | 'discussion'>('discussion')
  const [title, setTitle] = useState(''); const [body, setBody] = useState('')
  const load = () => teacherGroupApi.listPosts(gid).then(setPosts).catch(() => setPosts([]))
  useEffect(() => { load() }, [gid])
  const send = async () => { if (!body.trim()) return; await teacherGroupApi.addPost(gid, { kind, title, body }); setTitle(''); setBody(''); load() }
  const del = async (id: string) => { await teacherGroupApi.delPost(id); load() }

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-sky-100 bg-sky-50/60 p-3 text-sm text-slate-600">
        <b className="text-sky-800">5.1 Giao tiếp trong tổ chức:</b> dùng <b>Thông báo</b> cho kênh chính thức của tổ; dùng <b>Thảo luận</b> để trao đổi chuyên môn, thống nhất kế hoạch và lưu lại minh chứng giao tiếp số.
      </div>
      <div className="bg-white rounded-xl border border-gray-100 p-3">
        <div className="flex items-center gap-2 mb-2">
          <select value={kind} onChange={(e) => setKind(e.target.value as any)} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none">
            <option value="discussion">💬 Thảo luận</option><option value="notice">📢 Thông báo</option>
          </select>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tiêu đề (tuỳ chọn)" className="flex-1 p-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
        </div>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Nội dung trao đổi chuyên môn với tổ…" className="w-full p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 resize-y" />
        <button onClick={send} className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-bold hover:bg-teal-700"><MessageSquare size={15} /> Đăng</button>
      </div>
      {posts.length === 0 ? <p className="text-sm text-gray-400 p-3 text-center">Chưa có bài đăng nào.</p>
        : posts.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-gray-100 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
                {p.kind === 'notice' ? <Megaphone size={14} className="text-amber-600" /> : <MessageSquare size={14} className="text-sky-600" />}
                {p.title || (p.kind === 'notice' ? 'Thông báo' : 'Thảo luận')}
              </div>
              <button onClick={() => del(p.id)} className="text-rose-300 hover:text-rose-600"><Trash2 size={14} /></button>
            </div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{p.body}</div>
            <div className="text-[11px] text-slate-400 mt-1.5">{p.author_name} · {p.created_at ? new Date(p.created_at).toLocaleString('vi-VN') : ''}</div>
          </div>
        ))}
    </div>
  )
}

// ---- 5.3 Kho học liệu chung ----
const ResourcesTab: React.FC<{ gid: string; group: TGroup }> = ({ gid, group }) => {
  const [items, setItems] = useState<TResource[]>([])
  const [source, setSource] = useState<ResourceSource>('external')
  const [title, setTitle] = useState(''); const [ref, setRef] = useState(''); const [note, setNote] = useState('')
  const [usage, setUsage] = useState('Sinh hoạt chuyên môn')
  const [checks, setChecks] = useState({ reliable: true, legal: true, pedagogical: true })
  const physicsGroup = isPhysicsSubject(group.subject)
  const load = () => teacherGroupApi.listResources(gid).then(setItems).catch(() => setItems([]))
  useEffect(() => { load() }, [gid])
  useEffect(() => { if (!physicsGroup && source !== 'external') setSource('external') }, [physicsGroup, source])

  const sources: { id: ResourceSource; label: string; desc: string; enabled: boolean; kind: 'link' | 'lesson' | 'course'; placeholder: string }[] = [
    { id: 'external', label: 'Link ngoài', desc: 'Dùng cho mọi môn: Drive, YouTube, OER, website, PDF...', enabled: true, kind: 'link', placeholder: 'Dán link học liệu / Drive / YouTube / OER...' },
    { id: 'ebook-lesson', label: 'Ebook bài giảng', desc: 'Nguồn nội bộ hiện ưu tiên cho Vật lí.', enabled: physicsGroup, kind: 'lesson', placeholder: 'Slug hoặc ID bài giảng Ebook Vật lí' },
    { id: 'ebook-course', label: 'Ebook khóa học', desc: 'Khóa học nội bộ Vật lí trong ebook.', enabled: physicsGroup, kind: 'course', placeholder: 'Slug hoặc ID khóa học Ebook Vật lí' },
    { id: 'pmaker', label: 'PMaker', desc: 'Đề kiểm tra, ma trận, ngân hàng câu hỏi Vật lí.', enabled: physicsGroup, kind: 'link', placeholder: 'Dán link đề/ma trận PMaker' },
    { id: 'eng', label: 'Eng', desc: 'Video/bài học tiếng Anh chuyên ngành Vật lí.', enabled: physicsGroup, kind: 'link', placeholder: 'Dán link video hoặc bài học Eng' },
  ]
  const cur = sources.find(s => s.id === source) || sources[0]

  const add = async () => {
    if (!ref.trim() || !cur.enabled) return
    const cleanNote = note.trim()
    const reviewTags = [
      checks.reliable ? 'đã xét độ tin cậy' : 'cần kiểm tra độ tin cậy',
      checks.legal ? 'đã xét pháp lí' : 'cần kiểm tra pháp lí',
      checks.pedagogical ? 'phù hợp sư phạm' : 'cần thẩm định sư phạm',
    ].join('; ')
    const taggedNote = `[${cur.label}] [${usage}] ${reviewTags}${cleanNote ? ' - ' + cleanNote : ''}`
    const b: any = { resource_type: cur.kind, title, note: taggedNote }
    if (cur.kind === 'link') b.url = ref.trim(); else b.resource_id = ref.trim()
    try { await teacherGroupApi.addResource(gid, b); setTitle(''); setRef(''); setNote(''); setUsage('Sinh hoạt chuyên môn'); setChecks({ reliable: true, legal: true, pedagogical: true }); load() } catch {}
  }
  const safeUrl = (u: string) => /^https?:\/\//i.test(u) || u.startsWith('/') ? u : `https://${u}`
  const linkOf = (r: TResource) => r.resource_type === 'link' ? safeUrl(r.url) : r.resource_type === 'course' ? `/khoa-hoc/${r.resource_id}` : `/bai-giang/${r.resource_id}`
  const sourceLabel = (r: TResource) => (r.note || '').match(/^\[([^\]]+)\]/)?.[1] || (r.resource_type === 'course' ? 'Ebook khóa học' : r.resource_type === 'lesson' ? 'Ebook bài giảng' : 'Link ngoài')
  const usageLabel = (r: TResource) => (r.note || '').match(/^\[[^\]]+\]\s*\[([^\]]+)\]/)?.[1] || ''
  const cleanNote = (r: TResource) => (r.note || '').replace(/^\[[^\]]+\]\s*/, '').replace(/^\[[^\]]+\]\s*/, '')

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="font-black text-slate-800 mb-1 flex items-center gap-1.5"><BookOpen size={16} className="text-teal-600" /> Kho học liệu chung của tổ</div>
        <p className="text-sm text-slate-500 mb-3">Mọi môn đều chia sẻ được học liệu qua link ngoài. Với tổ Vật lí, có thể gắn thêm nguồn nội bộ từ Ebook, PMaker và Eng để tổ chức kho học liệu số theo năng lực 5.3.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-3">
          {sources.map(s => (
            <button key={s.id} type="button" disabled={!s.enabled} onClick={() => s.enabled && setSource(s.id)}
              className={`text-left rounded-xl border p-3 transition ${source === s.id ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-100' : s.enabled ? 'border-gray-200 hover:border-teal-300 bg-white' : 'border-gray-100 bg-gray-50 opacity-70 cursor-not-allowed'}`}>
              <div className="text-sm font-black text-slate-800">{s.label}</div>
              <div className="text-[11px] text-slate-500 mt-1">{s.desc}</div>
              {!s.enabled && <div className="text-[10px] font-bold text-amber-700 mt-2">Dành cho tổ Vật lí</div>}
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-teal-100 bg-teal-50/40 p-3 mb-3">
          <div className="text-xs font-black text-teal-800 uppercase mb-1">Đang thêm từ: {cur.label}</div>
          <div className="grid md:grid-cols-[1.2fr_1fr] gap-2">
            <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder={cur.placeholder} className="p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white" />
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tên học liệu hiển thị trong kho" className="p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white" />
          </div>
          <div className="grid md:grid-cols-[220px_1fr] gap-2 mt-2">
            <select value={usage} onChange={(e) => setUsage(e.target.value)} className="p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white">
              <option>Sinh hoạt chuyên môn</option>
              <option>Thiết kế bài dạy</option>
              <option>Dạy minh hoạ NCBH</option>
              <option>Ôn tập / giao nhiệm vụ</option>
              <option>Bồi dưỡng giáo viên</option>
            </select>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ghi chú: lớp, bài, mục tiêu, cách dùng trong tổ..." className="p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white" />
          </div>
          <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-600">
            <label className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-1"><input type="checkbox" checked={checks.reliable} onChange={(e) => setChecks({ ...checks, reliable: e.target.checked })} /> Độ tin cậy</label>
            <label className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-1"><input type="checkbox" checked={checks.legal} onChange={(e) => setChecks({ ...checks, legal: e.target.checked })} /> Pháp lí/bản quyền</label>
            <label className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-1"><input type="checkbox" checked={checks.pedagogical} onChange={(e) => setChecks({ ...checks, pedagogical: e.target.checked })} /> Phù hợp sư phạm</label>
          </div>
          <button onClick={add} disabled={!cur.enabled || !ref.trim()} className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-bold hover:bg-teal-700 disabled:opacity-50"><Plus size={15} /> Thẩm định & chia sẻ vào kho</button>
        </div>
      </div>

      {items.length === 0 ? <p className="text-sm text-gray-400 p-3 text-center">Kho học liệu của tổ còn trống.</p>
        : items.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3">
            <FolderOpen size={18} className="text-teal-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sm text-slate-800 truncate">{r.title || r.url || r.resource_id}</span>
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{sourceLabel(r)}</span>
                {usageLabel(r) && <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">{usageLabel(r)}</span>}
              </div>
              {cleanNote(r) && <div className="text-xs text-slate-500 truncate mt-0.5">{cleanNote(r)}</div>}
              <div className="text-[11px] text-slate-400">Chia sẻ bởi {r.shared_by_name}</div>
            </div>
            <a href={linkOf(r)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-sky-600 hover:text-sky-800"><ExternalLink size={13} /> Mở</a>
            <button onClick={async () => { await teacherGroupApi.delResource(r.id); load() }} className="text-rose-300 hover:text-rose-600"><Trash2 size={14} /></button>
          </div>
        ))}
    </div>
  )
}
// ---- Tự đánh giá năng lực số (khung 5 miền) + tổng hợp tổ ----
const CompetencyTab: React.FC<{ gid: string; isPro: boolean; onUpgrade: () => void }> = ({ gid, isPro, onUpgrade }) => {
  const [mine, setMine] = useState<Record<string, TSelfAssess>>({})
  const [summary, setSummary] = useState<TSummary | null>(null)
  const [evidence, setEvidence] = useState<TEvidence | null>(null)
  const load = async () => {
    const s = await teacherGroupApi.getSelf().catch(() => ({ items: [] as TSelfAssess[] }))
    const map: Record<string, TSelfAssess> = {}
    s.items.forEach((it) => { map[it.competency_code] = it })
    setMine(map)
    teacherGroupApi.summary(gid).then(setSummary).catch(() => setSummary(null))
    teacherGroupApi.evidence().then(setEvidence).catch(() => setEvidence(null))
  }
  useEffect(() => { load() }, [gid])

  const setLevel = async (code: string, level: number, note?: string) => {
    setMine((m) => ({ ...m, [code]: { competency_code: code, level, note: note ?? m[code]?.note ?? '', evidence: m[code]?.evidence ?? [] } }))
    await teacherGroupApi.saveSelf({ competency_code: code, level, note })
    teacherGroupApi.summary(gid).then(setSummary).catch(() => {})
  }

  const domains = Array.from(new Set(COMPETENCIES.map((c) => c.domain))).sort((a, b) => a - b)

  const renderCard = (c: typeof COMPETENCIES[number]) => {
    const locked = c.domain !== 5 && !isPro   // miền 1–4 cần Gói Tổ PRO
    if (locked) {
      return (
        <div key={c.code} className="bg-white rounded-2xl border border-amber-100 p-4 opacity-90">
          <div className="flex items-center justify-between gap-2">
            <div className="font-black text-slate-700"><span className="text-teal-600">{c.code}</span> · {c.name}</div>
            <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><Lock size={10} /> GÓI PRO</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Tự đánh giá Miền 1–4 thuộc Gói Tổ PRO.</p>
          <button onClick={onUpgrade} className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 hover:text-amber-900"><Crown size={13} /> Nâng cấp để mở khoá</button>
        </div>
      )
    }
    return (() => {
        const cur = mine[c.code]?.level || 0
        const sm = summary?.assessed?.[c.code]
        const ev = evidence?.[c.code]
        const nextLv = cur < 3 ? c.levels.find((l) => l.level === cur + 1) : undefined
        return (
          <div key={c.code} className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-black text-slate-800"><span className="text-teal-600">{c.code}</span> · {c.name}</div>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${LEVEL_TONE[cur]}`}>{LEVEL_LABELS[cur]}</span>
            </div>

            <div className="grid sm:grid-cols-3 gap-2 mt-3">
              {c.levels.map((lv) => (
                <button key={lv.level} onClick={() => setLevel(c.code, lv.level)}
                  className={`text-left rounded-xl border p-2.5 transition ${cur === lv.level ? 'border-teal-500 ring-2 ring-teal-100 bg-teal-50/40' : 'border-gray-200 hover:border-teal-300'}`}>
                  <div className="flex items-center gap-1.5 mb-1 text-xs font-black text-slate-700">
                    {cur === lv.level ? <ClipboardCheck size={13} className="text-teal-600" /> : <span className="w-3.5" />} Mức {lv.level} — {LEVEL_LABELS[lv.level]}
                  </div>
                  <ul className="text-[11px] text-slate-500 list-disc ml-4 space-y-0.5">
                    {lv.descriptors.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </button>
              ))}
            </div>

            <textarea value={mine[c.code]?.note || ''} onChange={(e) => setMine((m) => ({ ...m, [c.code]: { ...(m[c.code] || { competency_code: c.code, level: cur, evidence: [] }), note: e.target.value } }))}
              onBlur={(e) => setLevel(c.code, cur, e.target.value)} rows={2} placeholder="Minh chứng / ghi chú (vd: link bài giảng đã tạo, chuyên đề đã chia sẻ…)"
              className="w-full mt-2 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 resize-y" />

            {cur === 0 && <button onClick={() => setLevel(c.code, 1)} className="mt-1 text-xs font-bold text-teal-600">Bắt đầu tự đánh giá →</button>}

            {ev && (Object.values(ev.counts).some((n) => n > 0) || ev.suggestedLevel > 0) && (
              <div className="mt-2 rounded-lg bg-sky-50 border border-sky-100 p-2">
                <div className="text-[11px] font-bold text-sky-800 flex items-center gap-1">
                  <Activity size={12} /> Minh chứng tự động{ev.suggestedLevel > 0 && <span> · gợi ý mức {ev.suggestedLevel} ({LEVEL_LABELS[ev.suggestedLevel]})</span>}
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5 flex flex-wrap gap-x-3">
                  {Object.entries(ev.counts).map(([k, v]) => <span key={k}>{k}: <b>{v}</b></span>)}
                </div>
              </div>
            )}
            {nextLv && (
              <div className="mt-2 rounded-lg bg-amber-50/70 border border-amber-100 p-2">
                <div className="text-[11px] font-bold text-amber-800">🎯 Kế hoạch — để lên Mức {nextLv.level} ({LEVEL_LABELS[nextLv.level]}):</div>
                <ul className="text-[11px] text-slate-600 list-disc ml-4 mt-0.5 space-y-0.5">
                  {nextLv.descriptors.slice(0, 2).map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            )}
            {sm && sm.count > 0 && (
              <div className="mt-2 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                <Gauge size={13} className="text-teal-500" /> Cả tổ: mức TB <b className="text-slate-700">{sm.avg}</b> ({sm.count} GV đã tự đánh giá)
                <span className="text-slate-300">|</span> Cơ bản {sm.dist['1']} · Thành thạo {sm.dist['2']} · Nâng cao {sm.dist['3']}
              </div>
            )}
          </div>
        )
      })()
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 bg-teal-50/60 border border-teal-100 rounded-xl p-3">
        <b>Khung năng lực số của giáo viên.</b> Tự đánh giá mức đạt cho từng năng lực đặc trưng theo miền; hoạt động thật trên nền tảng (tạo bài giảng, chia sẻ học liệu, chuyên đề, thảo luận) là minh chứng cho các mức.
      </p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => teacherGroupApi.exportCompetency('docx').catch(() => {})} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-300 bg-white text-sky-700 text-sm font-bold hover:bg-sky-50"><FileDown size={15} /> Xuất hồ sơ (Word)</button>
        <button onClick={() => teacherGroupApi.exportCompetency('pdf').catch(() => {})} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-300 bg-white text-sky-700 text-sm font-bold hover:bg-sky-50"><FileDown size={15} /> PDF</button>
      </div>
      {domains.map((d) => (
        <div key={d} className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-black text-slate-700">
            <span className="w-6 h-6 rounded-lg bg-teal-600 text-white flex items-center justify-center text-xs">{d}</span>
            Miền {d}: {DOMAIN_NAMES[d]}
            {d !== 5 && !isPro && <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><Lock size={10} /> PRO</span>}
          </div>
          {COMPETENCIES.filter((c) => c.domain === d).map((c) => renderCard(c))}
        </div>
      ))}
    </div>
  )
}

// ---- Thành viên ----
const MembersTab: React.FC<{ g: TGroup & { members: TMember[]; isOwner?: boolean }; onChanged: () => void }> = ({ g, onChanged }) => {
  const [email, setEmail] = useState('')
  const isOwner = !!g.isOwner   // tổ trưởng THẬT / admin — mới được phân quyền
  const add = async () => { if (!email.trim()) return; await teacherGroupApi.addMember(g.id, email.trim()); setEmail(''); onChanged() }
  const toggleDeputy = async (m: TMember) => { await teacherGroupApi.setMemberRole(g.id, m.id, m.role === 'leader' ? 'member' : 'leader'); onChanged() }
  const makeLeader = async (m: TMember) => {
    if (!window.confirm(`Chuyển quyền TỔ TRƯỞNG cho ${m.name || m.email}? Bạn sẽ trở thành thành viên.`)) return
    try { await teacherGroupApi.transferLeader(g.id, m.id); onChanged() } catch (e: any) { alert(e?.message?.includes('400') ? 'Thành viên này chưa đăng nhập nên chưa thể làm tổ trưởng.' : 'Chuyển quyền chưa thành công.') }
  }
  return (
    <div className="space-y-3">
      {g.isLeader && (
        <div className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-2">
          <UserPlus size={16} className="text-teal-600 shrink-0" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email giáo viên để mời vào tổ" className="flex-1 p-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
          <button onClick={add} className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-bold hover:bg-teal-700">Thêm</button>
        </div>
      )}
      <div className="bg-white rounded-xl border border-gray-100 divide-y">
        {g.members.map((m) => {
          const deputy = m.role === 'leader' && !m.is_owner
          return (
            <div key={m.id} className="flex items-center gap-2 p-3">
              <div className="w-8 h-8 rounded-full bg-teal-50 text-teal-700 font-black flex items-center justify-center shrink-0">{(m.name || m.email)[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-slate-800 truncate">{m.name || m.email}</div>
                <div className="text-xs text-slate-400 truncate">{m.email}</div>
              </div>
              {m.is_owner && <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 shrink-0">Tổ trưởng</span>}
              {deputy && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 shrink-0">Tổ phó</span>}
              {isOwner && !m.is_owner && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggleDeputy(m)} title={deputy ? 'Bỏ tổ phó' : 'Đặt làm tổ phó'} className={`text-[11px] font-bold px-2 py-1 rounded-lg border ${deputy ? 'border-sky-300 text-sky-700 hover:bg-sky-50' : 'border-gray-200 text-slate-500 hover:bg-gray-50'}`}>{deputy ? 'Bỏ tổ phó' : 'Tổ phó'}</button>
                  <button onClick={() => makeLeader(m)} title="Chuyển quyền tổ trưởng" className="text-[11px] font-bold px-2 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50">Tổ trưởng</button>
                  <button onClick={async () => { await teacherGroupApi.removeMember(g.id, m.id); onChanged() }} className="text-rose-300 hover:text-rose-600"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TeacherGroupPage
